/* eslint-disable react-hooks/exhaustive-deps */
import dayjs from 'dayjs';
import CycleModuleRender from './cycleModuleRender';
import styles from 'styles/components/myCyclesDetails.module.scss';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Avatar,
  Badge,
  Button,
  Collapse,
  Drawer,
  Form,
  message,
  Radio,
} from 'antd';
import ReviewStatusIcon from 'components/reviewStatusIcon';
import { getTagFromReviewStatus, useWindowSize } from 'utils/utils';
import {
  CURRENT_REVIEW_DISPLAY,
  MODULES_ITEMS,
  REVIEW_STATUS,
} from 'utils/constants';
import {
  CheckCircleFilled,
  FileTextOutlined,
  UserOutlined,
  VerticalRightOutlined,
} from '@ant-design/icons';
import {
  retrieveContextReview,
  retrieveReviewsForCycle,
  submitReview,
  updateReview,
} from 'services/api/reviews';
import ProfileContext from 'services/profileContext';
import CycleDeadline from './cycleDeadline';

const CycleDetailsRender = ({
  myCycleId,
  cycle,
  setCycle,
  showConfettis,
  fetchCycle,
}) => {
  const { profile } = useContext(ProfileContext);

  const [review, setReview] = useState(null);
  const [form] = Form.useForm();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [contextDrawerOpen, setContextDrawerOpen] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [lastSavedDate, setLastSavedDate] = useState(null);
  const size = useWindowSize();
  const mobile = size.width < 1100;
  const [activeReviewIndex, setActiveReviewIndex] = useState(0);
  const [currentTask, setCurrentTask] = useState(null);
  const [currentModule, setCurrentModule] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [contextReviews, setContextReviews] = useState(null);
  const isMobile768 = size.width < 768;

  const [currentReviewDisplay, setCurrentReviewDisplay] = useState(
    searchParams.get('reviewDisplay') || CURRENT_REVIEW_DISPLAY.TODO
  );

  useEffect(() => {
    const newReviewDisplay = searchParams.get('reviewDisplay');
    if (newReviewDisplay && newReviewDisplay !== currentReviewDisplay) {
      setCurrentReviewDisplay(newReviewDisplay);
    }

    const reviewIndex = searchParams.get('reviewIndex');
    if (activeReviewIndex !== Number(reviewIndex || 0)) {
      setActiveReviewIndex(Number(reviewIndex || 0));
    }

    const reviewId = searchParams.get('reviewId');
    // reviewId=${review.from}-${review.to}-${review.moduleType}`;
    if (reviewId && reviewId.length > 0 && cycle?.reviews?.length > 0) {
      if (currentReviewDisplay === CURRENT_REVIEW_DISPLAY.TODO) {
        setCurrentReviewDisplay(CURRENT_REVIEW_DISPLAY.RECEIVED);
      } else {
        const [from, to, moduleType] = reviewId.split('-');
        //select the review
        const reviewIndex = cycle?.reviews?.findIndex((r) => {
          return (
            r.user._id === from && profile._id === to && r.type === moduleType
          );
        });
        if (reviewIndex !== -1) {
          setSearchParams((prev) => {
            prev.set('reviewIndex', reviewIndex);
            return prev;
          });
        }
      }
    }
  }, [searchParams]);

  useEffect(() => {
    const currentT =
      currentReviewDisplay === CURRENT_REVIEW_DISPLAY.TODO
        ? cycle?.tasks[Number(activeReviewIndex || 0)]
        : cycle?.reviews[Number(activeReviewIndex || 0)];

    if (
      currentReviewDisplay === CURRENT_REVIEW_DISPLAY.RECEIVED &&
      currentT?.status !== REVIEW_STATUS.DONE
    ) {
      setCurrentTask(null);
    } else {
      setCurrentTask(currentT);
    }

    setCurrentModule(cycle?.modules.find((m) => m.type === currentT?.type));
    fetchReview(cycle, activeReviewIndex);
  }, [activeReviewIndex, currentReviewDisplay]);


  const fetchReview = (cycleParam, reviewIndex) => {
    let task = null;
    // setCurrentReviewDisplay is async, so we can't rely on currentReviewDisplay
    // within this same execution after flipping it — use a local effective value.
    let effectiveDisplay = currentReviewDisplay;
    if (!cycleParam?.tasks || cycleParam?.tasks?.length === 0) {
      effectiveDisplay = CURRENT_REVIEW_DISPLAY.RECEIVED;
      setCurrentReviewDisplay(CURRENT_REVIEW_DISPLAY.RECEIVED);
      task = cycleParam?.reviews[reviewIndex];
    } else {
      task =
        effectiveDisplay === CURRENT_REVIEW_DISPLAY.TODO
          ? cycleParam?.tasks[reviewIndex]
          : cycleParam?.reviews[reviewIndex];
    }
    if (
      !task ||
      (effectiveDisplay === CURRENT_REVIEW_DISPLAY.RECEIVED && task.status === REVIEW_STATUS.TO_DO)
      || (effectiveDisplay === CURRENT_REVIEW_DISPLAY.RECEIVED && task.status === REVIEW_STATUS.IN_PROGRESS)|| (effectiveDisplay === CURRENT_REVIEW_DISPLAY.RECEIVED && task.status === REVIEW_STATUS.READY)) {
      return;
    }

    setReviewLoading(true);
    retrieveReviewsForCycle(
      effectiveDisplay === CURRENT_REVIEW_DISPLAY.TODO
        ? {
          from: profile._id,
          to: task.user._id,
          type: task.type,
          cycleId: myCycleId,
        }
        : {
          from: task.user._id,
          to: profile._id,
          type: task.type,
          cycleId: myCycleId,
        }
    )
      .then(({ data }) => {
        const newReview = {
          ...(data || {}),
          internalKey: data ? `${data?.to?._id}-${data?.moduleType}` : `${task.user._id}-${task.type}`
        }
        setReview(newReview);
        if (task.type === MODULES_ITEMS.DOWNWARD.type && effectiveDisplay === CURRENT_REVIEW_DISPLAY.TODO) {
          // fetch context reviews
          retrieveContextReview({
            from: profile._id,
            to: task.user._id,
            type: task.type,
            cycleId: myCycleId,
          })
            .then((contextReviewsData) => {
              setContextReviews(contextReviewsData);
            })
            .catch((err) => {
              console.error('err', err);
            })
            .finally(() => {
              // setReviewLoading(false);
            });
        }
        form.setFieldsValue({
          answers: data?.answers?.reduce((acc, answer) => {
            acc[answer.questionId] = answer.text;
            return acc;
          }, {}),
        });
      })
      .catch((err) => {
        console.error('err', err);
        message.error('Failed to fetch review');
      })
      .finally(() => {
        setReviewLoading(false);
      });
  };

  const openContext = () => {
    setContextDrawerOpen(true);
  };

  const onChange = (questionId, ans) => {
    if (saveLoading) {
      return;
    }
    const newAnswers =
      review?.answers?.filter((a) => a.questionId !== questionId) || [];
    newAnswers.push({ questionId, ...ans });

    setSaveLoading(true);
    return updateReview({
      from: profile._id,
      to: currentTask.user._id,
      type: currentTask.type,
      answers: newAnswers,
      cycleId: myCycleId,
    })
      .then(({ data }) => {
        setReview((prevR) => ({ ...prevR, answers: newAnswers }));
        setLastSavedDate(new Date());
        if (currentTask.status === REVIEW_STATUS.TO_DO) {
          // fetch cycle again to update status
          const newTasks = cycle.tasks.map((t) =>
            t.user._id === currentTask.user._id && t.type === currentTask.type
              ? { ...t, status: REVIEW_STATUS.IN_PROGRESS }
              : t
          );
          const tasksPerModule = newTasks.reduce((acc, task, index) => {
            acc[task.type] = [
              ...(acc[task.type] || []),
              { ...task, originalIndex: index },
            ];
            return acc;
          }, {});
          setCycle({
            ...cycle,
            tasks: newTasks,
            tasksPerModule,
          });
        }
      })
      .catch((err) => {
        console.error('err', err);
        message.error('Failed to save review');
      })
      .finally(() => {
        setSaveLoading(false);
      });
  };

  const submit = () => {
    setSubmitLoading(true);
    submitReview({
      from: profile._id,
      to: currentTask.user._id,
      type: currentTask.type,
      cycleId: myCycleId,
    })
      .then(({ data }) => {
        message.success('Review submitted');
        fetchCycle().then(() => { fetchReview(cycle, activeReviewIndex) });
        showConfettis();
      })
      .catch((err) => {
        console.error('err', err);
        message.error('Failed to submit review');
      })
      .finally(() => {
        setSubmitLoading(false);
      });
  };
  const onSaveFullReview = (values) => {
    const newAnswers = values.reduce((acc, answer) => {
      if (answer.value && answer.value.length > 0) {
        const c = review?.answers?.find(
          (a) => a.questionId === answer.name[1]
        )?.comment;
        acc.push({
          questionId: answer.name[1],
          text: answer.value,
          ...(c && c.length > 0 ? { comment: c } : {}),
        });
      }
      return acc;
    }, []);
    setSaveLoading(true);
    return updateReview({
      from: profile._id,
      to: currentTask.user._id,
      type: currentTask.type,
      answers: newAnswers,
      cycleId: myCycleId,
    })
      .then(({ data }) => {
        setReview((prevR) => ({ ...prevR, answers: newAnswers }));
        setLastSavedDate(new Date());
        if (currentTask.status === REVIEW_STATUS.TO_DO) {
          // fetch cycle again to update status
          const newTasks = cycle.tasks.map((t) =>
            t.user._id === currentTask.user._id && t.type === currentTask.type
              ? { ...t, status: REVIEW_STATUS.IN_PROGRESS }
              : t
          ).map((t) => ({ ...t, internalKey: `${t?.user?._id}-${t?.type}` }));
          const tasksPerModule = newTasks.reduce((acc, task, index) => {
            acc[task.type] = [
              ...(acc[task.type] || []),
              { ...task, originalIndex: index },
            ];
            return acc;
          }, {});
          setCycle(prevCycle => ({
            ...prevCycle,
            tasks: newTasks,
            tasksPerModule,
          }));
        }
      })
      .catch((err) => {
        console.error('err', err);
        message.error('Failed to save review');
      })
      .finally(() => {
        setSaveLoading(false);
      });
  };


  const reviewContextContent = () => {
    const rc = { context: contextReviews };
    if (!rc) {
      return null;
    }
    return (
      <>
        <h2 className={styles.contextTitle}>
          Other reviews submitted for this person for context
        </h2>
        {rc?.context?.length > 0 && (
          <div>
            <Collapse
              accordion
              className={styles.contextReviewsSelector}
              bordered={false}
              items={rc.context.map((review, i) => ({
                key: review._id,
                label: (
                  <div
                    className={styles.contextReviewItem}
                    style={{ display: 'flex', alignItems: 'flex-start' }}
                  >
                    <div style={{ flexGrow: 1 }}>
                      <div>
                        <FileTextOutlined />
                        &nbsp;
                        {MODULES_ITEMS[review.moduleType].label}
                      </div>
                      {review.from && (
                        <div className={styles.contextReviewItemLeft}>
                          By{' '}
                          <Avatar
                            src={review.from.profile.profilePictureUrl}
                            icon={<UserOutlined />}
                            size={24}
                          />
                          &nbsp;
                          {review.from.profile.firstName}{' '}
                          {review.from.profile.lastName}
                        </div>
                      )}
                    </div>
                    {review?.doneAt ? (
                      <div className={styles.lastSavedDateContext}>
                        <CheckCircleFilled />
                        &nbsp;
                        {dayjs(review.doneAt).format('DD/MM/YYYY')}
                      </div>
                    ) : (
                      <div style={{ color: 'grey' }}>Not submitted yet</div>
                    )}
                  </div>
                ),
                children: (
                  <div className={styles.contextContent}>
                    {review?.status !== REVIEW_STATUS.DONE ? (
                      <div style={{ color: 'grey' }}>
                        Review not submitted yet
                      </div>
                    ) : (
                      <CycleModuleRender
                        cycle={cycle}
                        hideTitle={true}
                        task={{
                          user: rc.context[i].from,
                        }}
                        myCycleId={myCycleId}
                        module={cycle?.modules.find(
                          (m) => m.type === rc.context[i].moduleType
                        )}
                        form={form}
                        review={rc.context[i]}
                        isReadOnly={true}
                      />
                    )}
                  </div>
                ),
              }))}
            />
          </div>
        )}
      </>
    );
  };

  const taskTodoOrInProgress = useMemo(() => {
    return cycle.tasks.filter(
      (t) =>
        t.status === REVIEW_STATUS.TO_DO ||
        t.status === REVIEW_STATUS.IN_PROGRESS
    );
  }, [cycle]);

  const sidebarContent = (
    <>
      <h1 style={{ fontSize: 24 }}>{cycle?.name}</h1>
      <CycleDeadline deadline={cycle.deadline} />
      <div className={styles.cycleStatus}>
        <Radio.Group
          onChange={(e) => {
            setCurrentReviewDisplay(e.target.value);
            setSearchParams((prev) => {
              prev.delete('reviewId');
              prev.set('reviewIndex', 0);
              prev.set('reviewDisplay', e.target.value);
              return prev;
            });
          }}
          value={currentReviewDisplay}
          options={[
            {
              label: 'Todo',
              value: 'todo',
              disabled: cycle.tasks.length === 0,
            },
            {
              label: 'Received',
              value: 'received',
              disabled: cycle.reviews.length === 0,
            },
          ]}
          optionType="button"
          buttonStyle="solid"
        />
      </div>
      <div className={styles.reviewsTodo}>
        {Object.keys(
          currentReviewDisplay === CURRENT_REVIEW_DISPLAY.TODO
            ? cycle.tasksPerModule
            : cycle.reviewsPerModule
        ).map((moduleType) => {
          const tasks =
            currentReviewDisplay === CURRENT_REVIEW_DISPLAY.TODO
              ? cycle.tasksPerModule[moduleType]
              : cycle.reviewsPerModule[moduleType];

          return (
            <div key={moduleType} className={styles.moduleWrapper}>
              <h2 className={styles.moduleTitle}>
                <FileTextOutlined style={{ marginRight: 8 }} />
                {MODULES_ITEMS[moduleType].labelShort}
              </h2>
              <div className={styles.reviewStatusList}>
                {tasks.map((task) => (
                  <div
                    className={`${styles.reviewStatus} ${activeReviewIndex === task.originalIndex
                        ? styles.active
                        : ''
                      }${task.status !== REVIEW_STATUS.DONE &&
                        currentReviewDisplay === CURRENT_REVIEW_DISPLAY.RECEIVED
                        ? styles.reviewStatusDisabled
                        : ''
                      }`}
                    key={task.type + task.originalIndex}
                    onClick={() => {
                      if (
                        currentReviewDisplay === CURRENT_REVIEW_DISPLAY.TODO ||
                        task.status === REVIEW_STATUS.DONE
                      ) {
                        setSearchParams((prev) => {
                          prev.delete('reviewId');
                          prev.set('reviewIndex', task.originalIndex);
                          return prev;
                        });
                      }
                    }}
                  >
                    <div className={styles.reviewStatusLeft}>
                      <div className={styles.reviewStatusIcon}>
                        <ReviewStatusIcon status={task.status} />
                      </div>
                      <div className={styles.reviewStatusTag}>
                        {getTagFromReviewStatus(task.status)}
                      </div>
                    </div>
                    <div className={styles.reviewStatusDirection}>
                      {currentReviewDisplay === CURRENT_REVIEW_DISPLAY.TODO
                        ? 'To'
                        : 'From'}
                    </div>
                    <div className={styles.reviewStatusLabel}>
                      <Avatar
                        size={34}
                        src={task.user.profile.profilePictureUrl}
                        icon={<UserOutlined />}
                      />
                      {`${task.user.profile.firstName} ${task.user.profile.lastName}`}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );

  return (
    <div className={styles.cycleDetails}>
      {isMobile768 ? (
        <Collapse
          items={[
            {
              key: 'sidebar',
              label: (
                <div>
                  {`${cycle?.name} tasks and reviews`}
                  {currentModule && currentModule.type ? (
                    <span>
                      &nbsp;{'>'}&nbsp;
                      <span style={{ fontWeight: 'bold' }}>{`${MODULES_ITEMS[currentModule.type]?.labelShort
                        } ${review && review.to
                          ? `(${review.to.profile.firstName}${review.to.profile.lastName
                            ? ` ${review.to.profile.lastName}`
                            : ''
                          })`
                          : ''
                        }`}</span>
                    </span>
                  ) : (
                    ''
                  )}
                  {taskTodoOrInProgress?.length > 0 && (
                    <Badge
                      count={taskTodoOrInProgress.length}
                      offset={[5, -10]}
                    />
                  )}
                </div>
              ),
              children: sidebarContent,
            },
          ]}
        />
      ) : (
        <div className={styles.sideWrapper}>{sidebarContent}</div>
      )}
      <div className={styles.contentWrapper}>
        {review && (
          <>
            {currentTask?.type === MODULES_ITEMS.DOWNWARD.type &&
              review.status !== REVIEW_STATUS.DONE &&
              currentReviewDisplay === CURRENT_REVIEW_DISPLAY.TODO &&
              contextReviews?.length > 0 &&
              mobile && (
                <>
                  <Drawer
                    width={500}
                    title="Context"
                    open={!!contextDrawerOpen}
                    onClose={() => {
                      setContextDrawerOpen(false);
                    }}
                  >
                    <div
                      className={styles.contextWrapper}
                      style={{
                        width: '100%',
                        maxWidth: '100%',
                        minWidth: '100%',
                      }}
                    >
                      {reviewContextContent()}
                    </div>
                  </Drawer>
                  <div style={{ textAlign: 'right' }}>
                    <Button
                      icon={<VerticalRightOutlined />}
                      type="primary"
                      ghost
                      onClick={() => {
                        openContext();
                      }}
                      className={styles.openContextButton}
                    >
                      Open context (
                      {contextReviews.length}{' '}
                      reviews)
                    </Button>
                  </div>
                </>
              )}
            <CycleModuleRender
              cycle={cycle}
              myCycleId={myCycleId}
              module={currentModule}
              form={form}
              review={review}
              lastSavedDate={lastSavedDate}
              saveLoading={saveLoading}
              submitLoading={submitLoading}
              reviewLoading={reviewLoading}
              submit={submit}
              onSaveFullReview={onSaveFullReview}
              task={currentTask}
              onChange={onChange}
              isReadOnly={
                currentReviewDisplay === CURRENT_REVIEW_DISPLAY.RECEIVED
              }
            />
          </>
        )}
      </div>
      {currentTask?.type === MODULES_ITEMS.DOWNWARD.type &&
        currentReviewDisplay === CURRENT_REVIEW_DISPLAY.TODO &&
        review?.status !== REVIEW_STATUS.DONE &&
        contextReviews?.length > 0 &&
        !mobile && (
          <div className={styles.contextWrapper}>
            {reviewContextContent()}
          </div>
        )}
    </div>
  );
};

export default CycleDetailsRender;
